<script lang="ts">
	import type { DatasheetPoiData, DateRange } from '$lib/components/pois/types';
	import NoRoomsImage from '$assets/availability/no-rooms.svg';
	import BookingLogo from '$assets/logos/booking-com.png';
	import { GetAccommodationAvailabilitiesStore } from '$houdini';
	import Link from '$lib/components/atoms/link/Link.svelte';
	import SkeletonText from '$lib/components/atoms/skeleton/SkeletonText.svelte';
	import ErrorCard from '$lib/components/error/ErrorCard.svelte';
	import HotelDateRangePicker from '$lib/components/molecules/date/hotelDateRangePicker/HotelDateRangePicker.svelte';
	import { MAX_SELECTION_DAYS, poiCategoryTrackerNames } from '$lib/components/pois/constants';
	import Room from '$lib/components/pois/datasheet/room/Room.svelte';
	import { isAccommodationPoi } from '$lib/components/pois/utils';
	import ViewMore from '$lib/components/viewMore/ViewMore.svelte';
	import { createBookingcomUrlWithLabel } from '$lib/services/bookingcom/urlWithLabel';
	import { track } from '$lib/services/tracking/googleAnalytics';
	import {
		trackOpenDatePickers,
		trackValidateDatePickers,
	} from '$lib/services/tracking/googleAnalytics/datepickers';
	import { getAccommodationAvailabilityEvent } from '$lib/services/tracking/googleAnalytics/tracking';
	import { getSearchParamsStore } from '$lib/services/urls/searchParams';
	import {
		type AugmentedHoudiniStore,
		augmentHoudiniStore,
	} from '$lib/utils/augmentedHoudiniStore';
	import { getDaysBetween, transformStringDateToCalendarDate } from '$lib/utils/date';
	import * as m from '$paraglide/messages';
	import { type CalendarDate } from '@internationalized/date';
	import { onMount } from 'svelte';

	const searchParams = getSearchParamsStore();
	interface Props {
		datasheetId?: string;
		poi: DatasheetPoiData;
		onSelectDates?: (datesRange: DateRange) => void;
		onClearDates?: () => void;
	}

	const {
		datasheetId,
		poi,
		onSelectDates,
		onClearDates,
	}: Props = $props();

	let overnightStay: number | undefined = $state(undefined);
	let checkin: CalendarDate | undefined = undefined;
	let checkout: CalendarDate | undefined = undefined;

	const queryStore: AugmentedHoudiniStore<GetAccommodationAvailabilitiesStore> = augmentHoudiniStore(new GetAccommodationAvailabilitiesStore());
	let hasRoomsPrices = $state(false);

	let translationForOvernightStay
		= $derived(!hasRoomsPrices || !overnightStay
			? m.poi_datasheet__availability__with()
			: getTranslationForOvernightStay());
	const accommodationAvailabilityDateRange = $derived($searchParams?.filters?.ACCOMMODATION_AVAILABILITY_DATE_RANGE as DateRange);

	onMount(() => {
		if (datasheetId) {
			getAvailability();
		}
	});

	const availabilityStatusCode = $derived($queryStore?.data?.getAccommodationAvailabilities?.availabilityStatusCode);

	function fetchGetAccommodationAvailabilitiesStore(
		datasheetId: string,
		checkin: CalendarDate,
		checkout: CalendarDate,
	) {
		queryStore
			.fetch({
				variables: {
					input: {
						checkin: checkin.toString(),
						checkout: checkout.toString(),
						guestsCount: 2,
						id: datasheetId,
					},
				},
			})
			.then((response) => {
				hasRoomsPrices
					= !!response.data?.getAccommodationAvailabilities?.highlightedRoomsPrices.length;
			})
			.catch(() => {
				hasRoomsPrices = false;
			});
	}

	function getTranslationForOvernightStay() {
		if (!overnightStay) {
			return;
		}
		return overnightStay === 1
			? m.poi_datasheet__availabilities__one__overnight_stay()
			: m.poi_datasheet__availabilities__many__overnight_stay({ count: overnightStay });
	}

	function handleSelectedDates(datesRange: CalendarDate[]) {
		checkin = datesRange[0];
		checkout = datesRange[1];

		if (!checkin || !checkout || !datasheetId) {
			return;
		}

		onSelectDates?.({ startDate: checkin.toString(), endDate: checkout.toString() });

		trackValidateDatePickers({
			context: 'datasheet',
			poiCategory: poiCategoryTrackerNames.ACCOMMODATION,
			searchParams: $searchParams,
			poi,
		});

		getAvailability();
	}

	function getBookUrl(bookUrl: string) {
		if (!isAccommodationPoi(poi)) {
			return bookUrl;
		};

		const departure = $searchParams?.itinerary?.at(0);
		const arrival = $searchParams?.itinerary?.at(-1);

		// TODO: previously pageName: 'accommodation.datasheet.panel',
		return createBookingcomUrlWithLabel().poi_datasheet_availability({
			poi,
			searchParams: $searchParams,
			arrival: arrival?.label || $searchParams?.selectedAddress?.address,
			departure: departure?.label,
		});
	}

	function getTransalationForViewMoreRoom(availableRoomsCount: number) {
		return availableRoomsCount === 1
			? m.poi_datasheet__availability__view__one__other()
			: m.poi_datasheet__availability__view__many__other({ count: availableRoomsCount });
	}
	function getAvailability() {
		const accommodationAvailabilityDateRange = $searchParams?.filters?.ACCOMMODATION_AVAILABILITY_DATE_RANGE as DateRange;
		checkin = transformStringDateToCalendarDate(accommodationAvailabilityDateRange?.startDate);
		checkout = transformStringDateToCalendarDate(accommodationAvailabilityDateRange?.endDate);

		if (!checkin || !checkout || !datasheetId) {
			return;
		}
		overnightStay = getDaysBetween(checkin, checkout);
		translationForOvernightStay = getTranslationForOvernightStay();
		fetchGetAccommodationAvailabilitiesStore(datasheetId, checkin, checkout);
	}

	function handleClickDates(inputId: string) {
		trackOpenDatePickers({
			context: 'datasheet',
			inputId,
			poiCategory: poiCategoryTrackerNames.ACCOMMODATION,
			searchParams: $searchParams,
		});
	}

	function handleRoomRedirection(roomPosition: number) {
		const offer = roomPosition.toString();
		track(getAccommodationAvailabilityEvent({ poi, searchParams: $searchParams, offer }));
	}

	function handleViewMoreClick() {
		track(
			getAccommodationAvailabilityEvent({ poi, searchParams: $searchParams, context: 'datasheet' }),
		);
	}
</script>

<div
	class="snap flex p-2 flex-col items-start gap-2 self-stretch rd-2 bg-neutral-weakest md:shadow-smooth-block"
>
	<div class="text-lg text-coloredneutral self-stretch">{m.poi_datasheet__availability()}</div>
	<div class="flex items-start gap-2 self-stretch">
		<HotelDateRangePicker
			shouldStayOpenAfterClearing
			withNumberOfNights
			maxSelectionDays={MAX_SELECTION_DAYS}
			onOpen={handleClickDates}
			onSelectDates={handleSelectedDates}
			{onClearDates}
			initialValues={{
				start: transformStringDateToCalendarDate(
					accommodationAvailabilityDateRange?.startDate,
				),
				end: transformStringDateToCalendarDate(
					accommodationAvailabilityDateRange?.endDate,
				),
			}}
		/>
	</div>
	{#if !$queryStore?.errors}
		<div class="w-full h-full justify-start items-center gap-0.5 inline-flex">
			<div class="text-coloredneutral text-sm">{translationForOvernightStay}</div>
			<img class="w-20 h-5" src={BookingLogo} alt="Booking.com" />
		</div>
	{/if}

	{#if $queryStore?.fetching && !$queryStore?.errors}
		<div class="flex p-2 items-start gap-2 rd-1 bg-neutral-weak">
			<SkeletonText
				minWords={3}
				maxWords={3}
				minWordLength={2}
				maxWordLength={10}
				class="flex-col"
			/>
		</div>
	{:else if !$queryStore?.errors}
		{#if $queryStore?.data?.getAccommodationAvailabilities?.highlightedRoomsPrices.length}
			<div class="flex items-center gap-2 self-stretch">
				{#each $queryStore?.data?.getAccommodationAvailabilities?.highlightedRoomsPrices as highlightedRoom, index (index)}
					{#if highlightedRoom}
						<Room
							{index}
							amount={highlightedRoom}
							currency={$queryStore.data.getAccommodationAvailabilities.currency}
							redirectUrl={getBookUrl($queryStore.data.getAccommodationAvailabilities.bookUrl)}
							onHandleRedirection={handleRoomRedirection}
						/>
					{/if}
				{/each}
				{#if $queryStore.data.getAccommodationAvailabilities.availableRoomsCount > $queryStore.data.getAccommodationAvailabilities.highlightedRoomsPrices.length}
					<Link
						button="ghost"
						href={getBookUrl($queryStore.data.getAccommodationAvailabilities.bookUrl)}
						target="_blank"
						rel="external"
						class="w-full"
						icon="east"
						iconPosition="right"
						label={getTransalationForViewMoreRoom(
							$queryStore.data.getAccommodationAvailabilities.availableRoomsCount
								- $queryStore.data.getAccommodationAvailabilities.highlightedRoomsPrices.length,
						)}
						onclick={(event) => {
							event.stopPropagation();
							const offer = 'others';
							track(getAccommodationAvailabilityEvent({ poi, searchParams: $searchParams, offer }));
						}}
					/>
				{/if}
			</div>
		{:else if $queryStore?.data?.getAccommodationAvailabilities?.bookUrl || availabilityStatusCode === 'AVAILABILITY_ERROR'}
			<div class="flex py-2 px-0 flex-col justify-center items-center gap-4 self-stretch">
				<ViewMore
					bookUrl={$queryStore?.data?.getAccommodationAvailabilities?.bookUrl
						? getBookUrl($queryStore.data.getAccommodationAvailabilities.bookUrl)
						: ''}
					image={NoRoomsImage}
					textViewMoreOn={m.view_more__on()}
					textViewMoreOnPartnerWebsite={m.view_more__on_partner_website()}
					altText={m.view_more__no_room()}
					textViewMoreGoToPartnerWebsite={m.poi_datasheet__availability__view_more__on_partner_website()}
					onHandleClick={handleViewMoreClick}
				/>
			</div>
		{/if}
	{:else if !!$queryStore?.errors}
		<ErrorCard
			errorImage={NoRoomsImage}
			text={m.poi_datasheet__availability__error()}
			onRetry={getAvailability}
		/>
	{/if}
</div>

<style>
	.snap {
		scroll-snap-align: start;
		scroll-snap-stop: always;
		scroll-margin: 272px;
	}
</style>
